import React, { useState, useRef, useEffect } from 'react';
import { OptionType } from '../../../utils/types';
import TextInput from '../TextInput/TextInput';
import AutocompleteOptions from './AutocompleteOptions';
import Icon from '../Icon/Icon';
import { useDebounce } from 'use-debounce';
import Title from '../Title/Title';

import './autocomplete.css';

type AutocompleteProps = {
    initialValue?: string;
    label?: string;
    error?: string | null;
    placeholder?: string;
    name: string;
    startSearch: (value: string) => Promise<void>;
    handleOptionClick: (field: string, result: OptionType) => void;
    isLoading: boolean;
    results: OptionType[] | null;
    isDisabled?: boolean;
    withReset?: boolean;
    onReset?: () => void;
    className?: string;
    formError?: string | null;
    iconName?: string;
};

const Autocomplete = ({
    initialValue = '',
    label,
    placeholder = '',
    error = null,
    name,
    startSearch,
    handleOptionClick,
    isLoading = false,
    results,
    isDisabled = false,
    withReset = false,
    onReset,
    className,
    formError,
    iconName,
}: AutocompleteProps) => {
    const MIN_SEARCH_LENGTH = 3;
    const [inputValue, setInputValue] = useState(initialValue);
    const [localLoading, setLocalLoading] = useState(isLoading);
    const [openMenu, setOpenMenu] = useState(false);
    const [optionClicked, setOptionClicked] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const hasUserTypedRef = useRef(false);
    const latestRequestIdRef = useRef(0);
    const startSearchRef = useRef(startSearch);

    useEffect(() => {
        startSearchRef.current = startSearch;
    }, [startSearch]);

    const startSearchHandler = async (value: string) => {
        const requestId = ++latestRequestIdRef.current;
        setLocalLoading(true);
        try {
            await startSearchRef.current(value);
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setLocalLoading(false);
            }
        }
    };

    const [debouncedValue] = useDebounce(inputValue, 500);

    useEffect(() => {
        setInputValue(initialValue);
        setOptionClicked(true);
    }, [initialValue]);

    useEffect(() => {
        const value = debouncedValue.trim();
        const initial = (initialValue || '').trim();

        if (!hasUserTypedRef.current || optionClicked) {
            return;
        }

        if (value.length < MIN_SEARCH_LENGTH || value === initial) {
            setOpenMenu(false);
            return;
        }

        setOpenMenu(true);
        startSearchHandler(value);
    }, [
        debouncedValue,
        initialValue,
        optionClicked,
    ]);

    useEffect(() => {
        if (!openMenu) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current) {
                return;
            }
            if (!containerRef.current.contains(event.target as Node)) {
                setOpenMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenu]);

    const inputChangeHandler = (_: string, value: string) => {
        hasUserTypedRef.current = true;
        setInputValue(value);
        setOptionClicked(false);

        if (!value.trim().length) {
            setOpenMenu(false);
        }
    };

    const openSearchMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
            return;
        }

        if (inputValue !== initialValue) {
            setOpenMenu(true);
        }
    };

    const onOptionClick = (result: OptionType) => {
        handleOptionClick(name, result);
        setInputValue(result.label);
        setOpenMenu(false);
        setOptionClicked(true);
    };

    const onResetClick = () => {
        hasUserTypedRef.current = false;
        setInputValue('');
        setOpenMenu(false);
        setOptionClicked(false);
        onReset && onReset();
    };

    return (
        <div className="input-container">
            {label && (
                <label htmlFor={name}>
                    <Title text={label} size="s" />
                </label>
            )}
            <div
                className={`autocomplete-container ${className || ''}`}
                ref={containerRef}
            >
                <TextInput
                    iconName={iconName || 'bx-search'}
                    value={inputValue}
                    type="text"
                    onChange={inputChangeHandler}
                    onClick={openSearchMenu}
                    name={name}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    autoComplete="one-time-code"
                    error={formError}
                />
                {withReset && (
                    <Icon name="bx-x" size="20px" onClick={onResetClick} />
                )}
                {openMenu && (
                    <div className="autocomplete-menu">
                        <AutocompleteOptions
                            error={error}
                            isLoading={isLoading || localLoading}
                            results={results}
                            inputValue={inputValue}
                            onOptionClick={onOptionClick}
                        />
                    </div>
                )}
            </div>
            {/* {formError && (
                <TextField color="red" size="s">
                    {formError}
                </TextField>
            )} */}
        </div>
    );
};

export default Autocomplete;
